// Imports
import { faCircle, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled, { css } from 'styled-components/macro';
import { View } from '..';
import { Plan } from '../../../../shareables/front-end/subscription-plans/plans-details';
import Button from '../../../resources/button';
import Pricing from './pricing';


// Styled components
const Wrapper = styled.div<{ containerWidth: string; backgroundColor: string; mostPopular: boolean }>`
	display: flex;
	width: ${({ containerWidth }) => containerWidth};
	padding: 1.875rem;
	flex-direction: column;
	align-items: flex-start;
	gap: 1.25rem;
	
	border-radius: 1.25rem;
	background-color: ${({ backgroundColor }) => backgroundColor};
	color: hsl(206, 25%, 20%);
	
	position: relative;
	
	> a:last-child {
		align-self: center;
	}
	
	@media (max-width: 48rem) {
		width: 100%;
		
		> a:last-child {
			width: 100%;
			justify-content: center;
		}
	}
	
	@media (max-width: 30rem) {
		padding-left: 1.5rem;
		padding-right: 1.5rem;
	}
	
	${({ mostPopular }) =>
		mostPopular
			? css`
					padding-top: 3.125rem;
				`
			: ''}
`;

const MostPopular = styled.div<{ backgroundColor: string }>`
	position: absolute;
	top: 0;
	left: 0;
	
	width: 100%;
	
	text-align: center;
	border-radius: 1.25rem 1.25rem 0 0;
	padding: 0.5rem 0;
	
	background-color: ${({ backgroundColor }) => backgroundColor};
	color: hsl(0, 0%, 100%);
	
	font-size: 0.875rem;
	font-weight: 600;
	line-height: 1;
	text-transform: uppercase;
`;

const HeadingContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.625rem;
	width: 100%;
`;

const Heading = styled.h2<{ color: string }>`
	color: ${({ color }) => color};
	font-size: 2.5rem;
	font-weight: 600;
	line-height: 1;
`;

const Description = styled.p`
	font-size: 1.25rem;
	line-height: 1.2;
`;

const FeaturesContainer = styled.figure`
	display: block;
`;

const FeaturesHeading = styled.figcaption`
	font-size: 0.875rem;
	font-weight: 400;
	line-height: 1;
	text-transform: uppercase;
	margin-bottom: 0.625rem;
`;

const Features = styled.ul`
	display: flex;
	gap: 0.625rem;
	list-style: none;
	flex-direction: column;
`;

const Feature = styled.li`
	display: flex;
	align-items: center;
	gap: 0.625rem;
	font-size: 1.125rem;
`;

const IconLayers = styled.span`
	position: absolute;
	top: 0;
	left: -0.129rem;
	
	font-size: 1.375rem;
`;

const Checkbox = styled.div`
	position: relative;
	
	width: 1.375rem;
	height: 1.375rem;
	min-width: 1.375rem;
`;


// Define the accepted props
interface Props extends Plan {
	view: View;
	sizeIndex: number;
	setSizeIndex: React.Dispatch<React.SetStateAction<number>>;
	mostPopular: boolean;
}


// Function component
const PaidPlan: React.FC<Props> = ({
	name,
	description,
	backgroundColor,
	accentColor,
	features,
	mostPopular,
	...state
}) => (
	<Wrapper
		containerWidth={mostPopular ? '23.75rem' : '20rem'}
		backgroundColor={backgroundColor}
		mostPopular={mostPopular}
	>
		{mostPopular && <MostPopular backgroundColor={accentColor}>Most popular</MostPopular>}
		
		<HeadingContainer>
			<Heading color={accentColor}>{name}</Heading>
			
			<Description>{description}</Description>
		</HeadingContainer>
		
		<Pricing {...state} plan={mostPopular ? 'engage' : 'inform'} />
		
		<FeaturesContainer>
			{features.label && <FeaturesHeading>{features.label}</FeaturesHeading>}
			
			<Features>
				{features.features.map(({ text: featureText }) => (
					<Feature key={featureText}>
						<Checkbox>
							<IconLayers className='fa-layers fa-fw'>
								<FontAwesomeIcon icon={faCircle} color={accentColor} />
								<FontAwesomeIcon icon={faCheck} color='hsl(0, 0%, 100%)' transform='shrink-6.36' />
							</IconLayers>
						</Checkbox>
						{featureText}
					</Feature>
				))}
			</Features>
		</FeaturesContainer>
		
		<Button
			backgroundColor={accentColor}
			fontColor='hsl(0, 0%, 100%)'
			displayArrowIndicator
			href={`https://start.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}`}
		>
			Start for free
		</Button>
	</Wrapper>
);

export default PaidPlan;
